import { useState, useEffect, useCallback, useMemo } from 'react'
import { WorkOrderCard } from '../components/work-orders/WorkOrderCard'
import { WorkOrderCompose } from '../components/work-orders/WorkOrderCompose'
import { WorkOrderDetail } from '../components/work-orders/WorkOrderDetail'
import type { WorkOrder, WorkOrderStatus } from '../types/work-orders'
import { fetchBrainWorkOrders, approveBrainWorkOrder, rejectBrainWorkOrder, getWSConfig } from '../api/brain'
import { WSClient } from '../lib/ws-client'

type FilterTab = 'all' | WorkOrderStatus

const TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending_approval', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'rejected', label: 'Rejected' },
]

const AUTO_REFRESH_MS = 30_000

export function WorkOrders() {
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([])
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date())
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null)
  const [showCompose, setShowCompose] = useState(false)
  const [liveConnected, setLiveConnected] = useState(false)
  const [lastLiveEventAt, setLastLiveEventAt] = useState<Date | null>(null)

  const loadWorkOrders = useCallback(async () => {
    setError(null)

    try {
      const data = await fetchBrainWorkOrders()
      setWorkOrders(Array.isArray(data) ? data : [])
      setLastRefreshed(new Date())
    } catch (err) {
      console.error('Work orders load error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load work orders')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Initial load
  useEffect(() => {
    loadWorkOrders()
  }, [loadWorkOrders])

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      loadWorkOrders()
    }, AUTO_REFRESH_MS)
    return () => clearInterval(interval)
  }, [loadWorkOrders])

  // Live stream for work-order updates
  useEffect(() => {
    let disposed = false
    let client: WSClient | null = null
    let unsubOpen: (() => void) | null = null
    let unsubClose: (() => void) | null = null
    let unsubMessage: (() => void) | null = null

    getWSConfig()
      .then(({ wsUrl, token }) => {
        if (disposed) return
        client = new WSClient(wsUrl, token)

        unsubOpen = client.on('open', () => {
          if (disposed) return
          setLiveConnected(true)
          client?.send({ type: 'subscribe_work_orders' })
        })
        unsubClose = client.on('close', () => {
          if (disposed) return
          setLiveConnected(false)
        })
        unsubMessage = client.on('message', (data: Record<string, unknown>) => {
          if (disposed) return
          if (data.type !== 'work_order_update') return
          const workOrder = data.workOrder as WorkOrder | undefined
          if (!workOrder) return

          setWorkOrders((prev) => {
            const matchIndex = prev.findIndex(
              (wo) =>
                (workOrder._doc_id && wo._doc_id === workOrder._doc_id) ||
                wo.id === workOrder.id,
            )
            if (matchIndex === -1) {
              return [workOrder, ...prev]
            }
            const next = [...prev]
            next[matchIndex] = workOrder
            return next
          })
          setLastRefreshed(new Date())
          setLastLiveEventAt(new Date())
        })
        client.connect()
      })
      .catch((err) => {
        if (disposed) return
        console.error('[work-orders] WS config failed:', err)
      })

    return () => {
      disposed = true
      try {
        client?.send({ type: 'unsubscribe_work_orders' })
      } catch {}
      unsubOpen?.()
      unsubClose?.()
      unsubMessage?.()
      client?.disconnect()
      setLiveConnected(false)
    }
  }, [])

  const handleApprove = async (id: string, notes?: string) => {
    await approveBrainWorkOrder(id, notes)
    // Reload to get updated state
    await loadWorkOrders()
  }

  const handleReject = async (id: string, notes?: string, reason?: string) => {
    await rejectBrainWorkOrder(id, reason || notes)
    await loadWorkOrders()
  }

  // Compute counts per tab
  const counts: Record<FilterTab, number> = {
    all: workOrders.length,
    pending_approval: 0,
    approved: 0,
    in_progress: 0,
    completed: 0,
    rejected: 0,
    failed: 0,
  }
  for (const wo of workOrders) {
    if (wo.status in counts) {
      counts[wo.status as FilterTab]++
    }
  }

  // Filter work orders by active tab
  const filtered =
    activeTab === 'all'
      ? workOrders
      : workOrders.filter((wo) => wo.status === activeTab)

  // Sort: pending first, then by received_at descending
  const sorted = [...filtered].sort((a, b) => {
    if (a.status === 'pending_approval' && b.status !== 'pending_approval') return -1
    if (b.status === 'pending_approval' && a.status !== 'pending_approval') return 1
    return new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
  })

  const brainOptions = useMemo(() => {
    const values = new Set<string>(['decisive-gm'])
    for (const wo of workOrders) {
      if (wo.to?.trim()) values.add(wo.to.trim())
      if (wo.from?.trim()) values.add(wo.from.trim())
    }
    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({
        value,
        label:
          value === 'decisive-gm'
            ? 'Decisive GM'
            : value === 'mech-browse-001'
              ? 'ThinkBrowse'
              : value,
      }))
  }, [workOrders])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-gray-500">Loading work orders...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-red mb-4">{error}</div>
        <button
          onClick={loadWorkOrders}
          className="text-swiss-small text-swiss-black underline underline-offset-2 cursor-pointer"
        >
          Retry
        </button>
      </div>
    )
  }

  // Detail view
  if (selectedWorkOrder) {
    return (
      <div>
        <WorkOrderDetail
          workOrder={selectedWorkOrder}
          onBack={() => setSelectedWorkOrder(null)}
          onUpdated={() => {
            setSelectedWorkOrder(null)
            loadWorkOrders()
          }}
        />
      </div>
    )
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-[24px] font-semibold tracking-tight leading-[1.2]">
            Work Orders
          </h1>
          <p className="text-swiss-gray-500 mt-2 text-[14px]">
            Review and approve agent work orders
          </p>
        </div>

        {/* Refresh button */}
        <div className="flex items-center gap-3">
          <span className="text-swiss-caption font-swiss-mono text-swiss-gray-400">
            Live {liveConnected ? 'ON' : 'OFF'}
            {lastLiveEventAt ? ` · ${lastLiveEventAt.toLocaleTimeString()}` : ''}
          </span>
          <span className="text-swiss-caption font-swiss-mono text-swiss-gray-400">
            {lastRefreshed.toLocaleTimeString()}
          </span>
          <button
            onClick={loadWorkOrders}
            className="px-3 py-1.5 text-[13px] border border-swiss-gray-200 text-swiss-black bg-transparent cursor-pointer transition-colors duration-150 hover:border-swiss-black"
            aria-label="Refresh work orders"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-0 border-b border-swiss-gray-200 mb-6" role="tablist" aria-label="Filter work orders by status">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key
          const count = counts[tab.key]
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              aria-controls="work-orders-list"
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-[13px] font-medium cursor-pointer transition-colors duration-150 border-b-2 -mb-px ${
                isActive
                  ? 'border-swiss-black text-swiss-black'
                  : 'border-transparent text-swiss-gray-500 hover:text-swiss-black'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span
                  className={`ml-2 inline-block min-w-[18px] text-center px-1 py-0 text-swiss-caption font-medium ${
                    isActive
                      ? 'bg-swiss-black text-swiss-white'
                      : 'bg-swiss-gray-200 text-swiss-gray-600'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Work Orders List */}
      <div id="work-orders-list" role="tabpanel" aria-label={`${activeTab === 'all' ? 'All' : activeTab} work orders`}>
        {sorted.length === 0 ? (
          <div className="bg-swiss-off-white border border-swiss-gray-200 p-12 text-center">
            <div className="text-swiss-body text-swiss-gray-500 mb-2">
              No work orders
            </div>
            <div className="text-swiss-small text-swiss-gray-400">
              {activeTab === 'all'
                ? 'No work orders have been received yet'
                : `No work orders with status "${activeTab.replace('_', ' ')}"`}
            </div>
          </div>
        ) : (
          <div>
            {sorted.map((wo) => (
              <WorkOrderCard
                key={wo.id}
                workOrder={wo}
                onApprove={handleApprove}
                onReject={handleReject}
                onClick={setSelectedWorkOrder}
              />
            ))}
          </div>
        )}
      </div>

      {/* Compose FAB */}
      <button
        onClick={() => setShowCompose(true)}
        className="fixed bottom-6 right-6 w-12 h-12 bg-swiss-black text-swiss-white flex items-center justify-center text-swiss-h1 cursor-pointer z-10 hover:bg-swiss-gray-700 transition-colors duration-150"
        aria-label="Create new work order"
      >
        +
      </button>

      {/* Compose Modal */}
      {showCompose && (
        <WorkOrderCompose
          onClose={() => setShowCompose(false)}
          brainOptions={brainOptions}
          onCreated={() => {
            setShowCompose(false)
            loadWorkOrders()
          }}
        />
      )}
    </div>
  )
}
